import { useEffect, useState } from 'react'

import { FavouriteIcon } from '@hugeicons/react'
import clsx from 'clsx'
import _ from 'lodash'

import { api } from '@/shared/lib/api'
import { showErrorAlert } from '@/shared/lib/errors'
import { resolveReactionCount } from '@/shared/lib/reactions/resolveReactionCount'

const isLikedReaction = reacted =>
  reacted === 'like' || reacted?.kind === 'like'

/**
 * Curtida em cards — pill compacto com ícone + contador lado a lado.
 */
export const HeartReactionButton = ({
  id,
  reactableType,
  reacted = null,
  reactions = {},
  onReactionChange = () => {},
  likeAriaLabel = 'Curtir',
  unlikeAriaLabel = 'Remover curtida',
  layout = 'rail',
  className = ''
}) => {
  const likedFromProps = isLikedReaction(reacted)
  const [isLiked, setIsLiked] = useState(likedFromProps)
  const [likeCount, setLikeCount] = useState(reactions?.like ?? 0)

  useEffect(() => {
    setIsLiked(isLikedReaction(reacted))
  }, [reacted])

  useEffect(() => {
    setLikeCount(reactions?.like ?? 0)
  }, [reactions?.like])

  const handleToggleReaction = async event => {
    event.preventDefault()
    event.stopPropagation()

    const nextLiked = !isLiked
    const nextCount = nextLiked
      ? likeCount + 1
      : Math.max(0, likeCount - 1)

    setIsLiked(nextLiked)
    setLikeCount(nextCount)

    try {
      const res = await api.post('/user/reactions/toggle', {
        record: {
          reactable_id: id,
          reactable_type: reactableType,
          kind: 'like'
        }
      })

      const record = _.get(res, 'data.record', null)
      const updatedReactions = _.get(res, 'data.reactions', null)
      const newUserReaction = record?.kind || null

      setIsLiked(newUserReaction === 'like')
      setLikeCount(
        resolveReactionCount(nextCount, updatedReactions ?? { like: nextCount })
      )
      onReactionChange(
        updatedReactions ?? { ...reactions, like: nextCount },
        newUserReaction
      )
    } catch (error) {
      setIsLiked(likedFromProps)
      setLikeCount(reactions?.like ?? 0)
      showErrorAlert(error)
    }
  }

  const isRail = layout === 'rail'

  return (
    <button
      type="button"
      aria-label={isLiked ? unlikeAriaLabel : likeAriaLabel}
      aria-pressed={isLiked}
      onClick={handleToggleReaction}
      className={clsx(
        'relative inline-flex shrink-0 items-center justify-center border border-solid transition-all duration-150',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-ring)]',
        'motion-safe:active:scale-[0.96]',
        'after:absolute after:-inset-2 after:content-[""]',
        isRail
          ? 'gap-1 rounded-lg px-1.5 py-1.5'
          : 'gap-1.5 rounded-lg px-2.5 py-1.5',
        isLiked
          ? 'border-[var(--color-like-pill-active-border,#f5b8c4)] bg-[var(--color-like-pill-active-surface,#ffe8ec)] text-[var(--color-danger-text,#b83a48)]'
          : 'border-[var(--color-like-pill-border)] bg-[var(--color-like-pill-surface)] text-gray-600 shadow-sm hover:border-[var(--color-like-pill-hover-border)] hover:bg-[var(--color-like-pill-hover-surface)] hover:text-brand',
        className
      )}
    >
      <FavouriteIcon
        size={isRail ? 14 : 16}
        variant={isLiked ? 'solid' : 'stroke'}
        className={clsx(
          'relative z-[1] shrink-0 transition-colors duration-150',
          isLiked ? 'text-[var(--color-danger,#f64e60)]' : 'text-gray-500'
        )}
        aria-hidden
      />

      {likeCount > 0 && (
        <span
          className={clsx(
            'relative z-[1] font-bold leading-none tabular-nums',
            isRail ? 'text-xs' : 'text-sm',
            isLiked ? 'text-[var(--color-danger-text,#b83a48)]' : 'text-gray-700'
          )}
        >
          {likeCount}
        </span>
      )}
    </button>
  )
}
